import {
  FormStatus,
  OnboardingStep,
  RouteBuilders,
  DonationFormCategory,
  FormCategory,
  TicketingFormCategory,
  isDonationFormCategory,
} from "@simplyk/common";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "../constants/amplitude";

import { useAmplitude } from "./amplitude/useAmplitude";
import { useGetCrossSellDonationFormInput } from "./useGetCrossSellDonationFormInput";
import { useGetCrossSellTicketingInput } from "./useGetCrossSellTicketingInput";
import { useTranslate } from "./useTranslate";

import { trpc } from "@/common/helpers/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useCreateDonationFormInput } from "@/features/LiveFormEditor/common/useCreateDonationFormInput";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useCreateTicketingInput } from "@/features/LiveFormEditor/common/useCreateTicketingInput";

export type FormCreationSource =
  | "service_entry"
  | "recommended_campaign_types"
  | "cross_sell"
  | "insights_empty_state"
  | "organization_assistant";

export const useCreateForm = ({
  isOnboarding,
  isCrossSell,
  source,
}: {
  isOnboarding: boolean;
  isCrossSell?: boolean;
  source: FormCreationSource;
}) => {
  const router = useRouter();
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const utils = trpc.useUtils();

  const markCampaignListStale = () =>
    utils.getCampaignListPagedTableData.invalidate(undefined, { refetchType: "none" });

  const { mutateAsync: createDraftDonationForm } = trpc.createDraftDonationForm.useMutation();
  const { mutateAsync: createDraftCampaign } = trpc.createDraftCampaign.useMutation();
  const { mutateAsync: createDraftTicketing } = trpc.createDraftTicketing.useMutation();
  const { mutateAsync: updateOnboardingStep } = trpc.updateOnboardingStep.useMutation();

  const getDonationFormInput = useCreateDonationFormInput();
  const getTicketingInput = useCreateTicketingInput();
  const getCrossSellDonationFormInput = useGetCrossSellDonationFormInput();
  const getCrossSellTicketingInput = useGetCrossSellTicketingInput();

  const logFormDraftCreated = (formCategory: FormCategory, formId: string) => {
    logAmplitudeEvent(AmplitudeEvents.FormDraftCreated, {
      form_id: formId,
      form_type: formCategory,
      is_onboarding: isOnboarding,
      source,
      is_sweepstakes: formCategory === FormCategory.Sweepstakes,
      ...((formCategory === FormCategory.Lottery || formCategory === FormCategory.Sweepstakes) && {
        lottery_type: formCategory === FormCategory.Sweepstakes ? "sweepstakes" : "raffle",
      }),
    });
  };

  const createDonationFormAndRedirect = async (donationCategory: DonationFormCategory) => {
    const baseInput = getDonationFormInput(donationCategory);

    let input = baseInput;
    if (isCrossSell) {
      const crossSellContent = getCrossSellDonationFormInput(donationCategory, t);
      input = {
        ...baseInput,
        donationFormFields: [
          {
            ...baseInput.donationFormFields[0],
            ...(crossSellContent.title && { title: crossSellContent.title }),
            ...(crossSellContent.description && { description: crossSellContent.description }),
          },
        ],
      };
    }

    const createFn =
      donationCategory === DonationFormCategory.PeerToPeerCampaign
        ? () => createDraftCampaign({ ...input, status: FormStatus.Draft, isCrossSell })
        : () => createDraftDonationForm({ ...input, isCrossSell });

    const { data, error } = await createFn();
    if (error || !data?.id) {
      enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), { vibe: "danger" });
      return;
    }
    logFormDraftCreated(donationCategory, data.id);
    await markCampaignListStale();

    const url = RouteBuilders.buildCreateNewDonationFormLink()
      .addSearchParams({
        donationFormId: data.id,
        formCategory: donationCategory,
        ...(isOnboarding && { isOnboarding: "true" }),
      })
      .toString();

    return await router.push(url);
  };

  const createTicketingFormAndRedirect = async (ticketingCategory: TicketingFormCategory) => {
    const baseInput = getTicketingInput(ticketingCategory);

    let input = baseInput;
    if (isCrossSell && ticketingCategory !== TicketingFormCategory.Sweepstakes) {
      const crossSellContent = getCrossSellTicketingInput(ticketingCategory, t);
      input = {
        ...baseInput,
        ticketingFields: [
          {
            ...baseInput.ticketingFields[0],
            ...(crossSellContent.title && { title: crossSellContent.title }),
            ...(crossSellContent.description && { description: crossSellContent.description }),
          },
        ],
      };
    }

    const { data: ticketingId, error } = await createDraftTicketing({ ...input, isCrossSell });
    if (error || !ticketingId) {
      enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), { vibe: "danger" });
      return;
    }
    logFormDraftCreated(ticketingCategory, ticketingId);
    await markCampaignListStale();

    const url = RouteBuilders.buildCreateNewTicketingFormLink()
      .addSearchParams({
        ticketingId,
        formCategory: ticketingCategory,
        ...(isOnboarding && { isOnboarding: "true" }),
      })
      .toString();

    return await router.push(url);
  };

  const createFormAndRedirect = async (formCategory: FormCategory) => {
    try {
      if (isOnboarding) {
        await updateOnboardingStep({ onboardingStep: OnboardingStep.FormCreation });
      }

      return await (isDonationFormCategory(formCategory)
        ? createDonationFormAndRedirect(formCategory)
        : createTicketingFormAndRedirect(formCategory));
    } catch {
      enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), { vibe: "danger" });
    }
  };

  return { createFormAndRedirect };
};
